import React, { useState, useEffect } from "react";
import {
  Modal,
  Alert,
  Checkbox,
  Form,
  Input,
  Card,
  Divider,
  Button,
  Typography,
  Space,
  Spin,
  message,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import { useNavigate, useLocation } from "react-router-dom";
import { useTable } from "../Table/TableContext";
import { useCheckout } from "../../../layouts/checkout/CheckoutContext";

const { Text, Title } = Typography;

const VoucherTableGuestCheckout = (props: any) => {
  const { checkPromotionEligibility } = useCheckout();
  const { currentBooking, createVoucherTableBooking } = useTable();
  const location = useLocation();
  const navigate = useNavigate();

  // Get booking data from navigation state
  const { verifiedVouchers, bookingData, restaurant }: any =
    location.state || {};

  // Form states
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [specialRequest, setSpecialRequest] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [payingNow, setPayingNow] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Promo code states
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState<any>(null);
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [promoError, setPromoError] = useState("");

  // Referral code state
  const [manualReferral, setManualReferral] = useState("");

  // Calculate pricing
  const calculatePrice = () => {
    const depositAmount: any =
      // @ts-ignore
      currentBooking?.bookingParams?.deposit_amount ||
      bookingData?.bookingParams?.deposit_amount ||
      0;

    return {
      deposit: parseFloat(depositAmount),
      total: parseFloat(depositAmount),
    };
  };

  const prices = calculatePrice();

  const calculateFinalPrice = () => {
    const basePrices = calculatePrice();
    let discountAmount = 0;

    if (appliedPromo && appliedPromo.eligible === "YES") {
      discountAmount = parseFloat(appliedPromo.discount_amount || "0");
    }

    return {
      baseTotal: basePrices.total,
      discountAmount: discountAmount,
      finalTotal: Math.max(0, basePrices.total - discountAmount),
    };
  };

  const finalPrices = calculateFinalPrice();

  // Promo code functions
  const handleApplyPromo = async () => {
    if (!promoCode.trim()) {
      setPromoError("Please enter a promo code");
      return;
    }

    setApplyingPromo(true);
    setPromoError("");

    try {
      const result = await checkPromotionEligibility({
        ordertype: "table",
        promotion_code: promoCode.trim(),
        amount: finalPrices.baseTotal,
      });

      if (result.eligible === "YES") {
        setAppliedPromo(result);
        message.success(`Promo code applied! ${result.description}`);
      } else {
        setPromoError(result.description || "This promo code is not eligible");
      }
    } catch (err: any) {
      setPromoError(err.message || "Failed to apply promo code");
      message.error("Failed to apply promo code");
    } finally {
      setApplyingPromo(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoCode("");
    setPromoError("");
    message.info("Promo code removed");
  };

  const onChangeInput = (type: any, value: any) => {
    const setters: any = {
      name: setName,
      email: setEmail,
      phone: setPhone,
      specialRequest: setSpecialRequest,
    };

    if (setters[type]) {
      setters[type](value);
    }
  };

  const onPayNow = async () => {
    if (!name || !email || !phone) {
      message.error("Please fill in all required contact information");
      return;
    }

    if (!agreed) {
      message.error("Please agree to the terms and conditions");
      return;
    }

    setPayingNow(true);

    const bookingParams =
      currentBooking?.bookingParams || bookingData?.bookingParams;

    const params = {
      store_id: restaurant?.store_id || bookingParams?.store_id,
      datereserve: bookingParams?.datereserve,
      timereserve: bookingParams?.timereserve,
      number_adult: bookingParams?.number_adult,
      number_child: bookingParams?.number_child,
      name: name,
      email: email,
      phone: phone,
      promotion_code: appliedPromo?.code || "",
      referral: manualReferral || "",
      voucher_ids: verifiedVouchers?.map((v: any) => v.voucher_code) || [],
      voucher_ticket: 1, // Flag for voucher booking
    };

    console.log("Guest voucher table booking params:", params);

    try {
      const res = await createVoucherTableBooking(params);
      if (res && res.payment_redirect_url) {
        setShowPaymentModal(true);
        // Give user time to read the message
        setTimeout(() => {
          window.location.replace(`${res.payment_redirect_url}`);
        }, 2500);
      }
    } catch (error) {
      console.error("Guest voucher payment error:", error);
      setShowPaymentModal(false);
      message.error("Failed to process booking. Please try again.");
    } finally {
      setPayingNow(false);
    }
  };

  // Calculate voucher summary
  const getVoucherSummary = () => {
    if (!verifiedVouchers || verifiedVouchers.length === 0) return null;

    const adultCount = verifiedVouchers.filter((v: any) =>
      v.price_type?.toLowerCase().includes("adult")
    ).length;

    const childCount = verifiedVouchers.filter((v: any) =>
      v.price_type?.toLowerCase().includes("child")
    ).length;

    return { adultCount, childCount, total: verifiedVouchers.length };
  };

  const voucherSummary = getVoucherSummary();

  // Get booking details for display
  const displayBooking = currentBooking || bookingData;

  return (
    <div id="HomeScreen">
      {/* Payment Processing Modal */}
      {/* @ts-ignore */}
      <Modal
        title="Processing Your Payment"
        visible={showPaymentModal}
        footer={null}
        closable={false}
        centered
        maskClosable={false}
      >
        <div style={{ textAlign: "center", padding: "20px" }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>🔒</div>
          <Title level={4}>Secure Payment</Title>
          <Text>You're being redirected to our secure payment gateway.</Text>
          <br />
          <br />
          <Text type="secondary">
            • Please complete your payment in the new window
            <br />
            • Do not close this page until payment is complete
            <br />• You'll be redirected back here after payment
          </Text>
          <br />
          <br />
          <div style={{ marginTop: "16px" }}>
            <Spin size="large" />
          </div>
        </div>
      </Modal>

      <div className="container">
        <div style={{ marginBottom: "24px" }}>
          <Button
            type="link"
            onClick={() => navigate(-1)}
            style={{ paddingLeft: 0 }}
          >
            ← Back to Table Selection
          </Button>
          <Title level={3}>Complete Your Table Booking</Title>
          <Text type="secondary">
            Using {verifiedVouchers?.length || 0} vouchers
          </Text>
        </div>

        <div>
          <Text>
            Please fill in the details below so that we can email or call you if
            we have any enquiries regarding your information or booking. Thank
            you!
          </Text>
        </div>

        {/* Voucher Booking Alert */}
        <Alert
          message={
            <div>
              <div style={{ fontWeight: "bold", marginBottom: 8 }}>
                Your Voucher Table Booking Details
              </div>

              <div>
                <b>
                  {/* @ts-ignore */}
                  {displayBooking?.bookingParams?.store_name ||
                    restaurant?.name}
                </b>
              </div>

              <div>
                Date:{" "}
                <b>
                  {moment(displayBooking?.bookingParams?.datereserve).format(
                    "DD MMM YYYY"
                  )}
                </b>{" "}
                at
                {/* @ts-ignore */}
                <b> {displayBooking?.bookingParams?.timereserve}</b>
              </div>

              <div>
                <b>{displayBooking?.bookingParams?.number_adult} Adults</b>
                {(displayBooking?.bookingParams?.number_child || 0) > 0 && (
                  <>
                    {" & "}
                    <b>
                      {displayBooking?.bookingParams?.number_child} Children
                    </b>
                  </>
                )}
              </div>

              {/* Voucher Summary */}
              {voucherSummary && (
                <div>
                  <Text strong>Vouchers: </Text>
                  <Text>
                    {voucherSummary.adultCount > 0 &&
                      `${voucherSummary.adultCount} Adult vouchers`}
                    {voucherSummary.adultCount > 0 &&
                      voucherSummary.childCount > 0 &&
                      ", "}
                    {voucherSummary.childCount > 0 &&
                      `${voucherSummary.childCount} Child vouchers`}
                    {` (${voucherSummary.total} total)`}
                  </Text>
                </div>
              )}

              <Divider style={{ margin: "10px 0" }} />
              <div
                style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}
              >
                *This table booking is free of charge
              </div>
            </div>
          }
          type="success"
          style={{ marginTop: "1em", marginBottom: "1em" }}
        />

        <Form layout="vertical">
          <Form.Item label="Full Name" required>
            <Input
              value={name}
              onChange={(e) => {
                onChangeInput("name", e.target.value);
              }}
              style={{ width: "100%" }}
              placeholder="John Doe"
            />
          </Form.Item>
          <Form.Item label="Email" required>
            <Input
              value={email}
              onChange={(e) => {
                onChangeInput("email", e.target.value);
              }}
              style={{ width: "100%" }}
              placeholder="johndoe@mail.com"
            />
          </Form.Item>
          <Form.Item label="Phone No" required>
            <Input
              value={phone}
              onChange={(e) => {
                onChangeInput("phone", e.target.value);
              }}
              addonBefore={"+6"}
              placeholder="0123456789"
              style={{ width: "100%" }}
            />
          </Form.Item>

          {/* Special Request */}
          <Form.Item label="Special Request (optional)">
            <Input.TextArea
              value={specialRequest}
              onChange={(e) => {
                onChangeInput("specialRequest", e.target.value);
              }}
              placeholder="Any special requests or dietary requirements..."
              rows={3}
            />
          </Form.Item>

          {/* Referral Code Section */}
          <Form.Item label="Referral Code (optional)">
            <Input
              value={manualReferral}
              onChange={(e) => setManualReferral(e.target.value)}
              style={{ width: "100%" }}
              placeholder="Enter referral code (optional)"
            />
          </Form.Item>

          {/* Promo Code Section */}
          <Form.Item label="Promo Code (optional)">
            <div
              style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}
            >
              <Input
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                placeholder="Enter promo code"
                disabled={applyingPromo || !!appliedPromo}
                style={{ flex: 1 }}
              />
              {appliedPromo ? (
                <Button onClick={handleRemovePromo} danger type="default">
                  Remove
                </Button>
              ) : (
                <Button
                  onClick={handleApplyPromo}
                  loading={applyingPromo}
                  type="primary"
                  disabled={!promoCode.trim()}
                >
                  Apply
                </Button>
              )}
            </div>
            {promoError && (
              <Text
                type="danger"
                style={{ fontSize: "12px", marginTop: "4px", display: "block" }}
              >
                {promoError}
              </Text>
            )}
            {appliedPromo && appliedPromo.eligible === "YES" && (
              <div style={{ marginTop: "4px" }}>
                <Text
                  type="success"
                  style={{ fontSize: "12px", display: "block" }}
                >
                  ✅ {appliedPromo.description}
                </Text>
                <Text
                  type="secondary"
                  style={{ fontSize: "11px", display: "block" }}
                >
                  Valid until:{" "}
                  {moment(appliedPromo.expirydate).format("DD MMM YYYY")}
                </Text>
              </div>
            )}
          </Form.Item>
        </Form>

        {/* Terms and Conditions */}
        <Checkbox
          onChange={(e) => {
            setAgreed(e.target.checked);
          }}
          checked={agreed}
          style={{ marginRight: 10, marginTop: 20 }}
        >
          I agree with the <a href="#">terms and conditions</a> of this company
        </Checkbox>

        <Divider />

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            style={{ marginRight: 10 }}
            onClick={() => {
              navigate(-1);
            }}
          >
            Back
          </Button>
          <Button
            type="primary"
            onClick={onPayNow}
            loading={payingNow}
            disabled={!agreed}
            title={
              !agreed ? "Please agree to terms and conditions to proceed" : ""
            }
          >
            Book Now
          </Button>
        </div>
        <Divider />
      </div>
    </div>
  );
};

export default VoucherTableGuestCheckout;
